/**
 * Relative date-range presets for HazardAI scheduled panel reports.
 *
 * The token is stored in Report.filters.dateRange and resolved to concrete
 * YYYY-MM-DD boundaries (in the company's timezone) at report-generation time
 * by the backend's resolveRelativeDateRange() helper.
 *
 * Keep in sync with backend/src/services/Helper/date.ts — HazardAIDateRangePreset.
 */

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'custom';

export const DATE_RANGE_PRESETS: Array<{
  value: DateRangePreset;
  label: string;
}> = [
  {value: 'today', label: 'Today'},
  {value: 'yesterday', label: 'Yesterday'},
  {value: 'last_7_days', label: 'Last 7 Days'},
  {value: 'last_30_days', label: 'Last 30 Days'},
  {value: 'this_week', label: 'This Week (Mon–Sun)'},
  {value: 'last_week', label: 'Last Week (Mon–Sun)'},
  {value: 'this_month', label: 'This Month'},
  {value: 'last_month', label: 'Last Month'},
  {value: 'custom', label: 'Custom (fixed dates)'},
];

/**
 * Presets shown on live dashboard panels where dates are resolved client-side
 * via resolveDateRangePreset(). Weeks are Mon–Sun; "This Year" is year-to-date.
 */
export const PANEL_DATE_RANGE_PRESETS: Array<{
  value: DateRangePreset;
  label: string;
}> = [
  {value: 'today', label: 'Today'},
  {value: 'yesterday', label: 'Yesterday'},
  {value: 'this_week', label: 'This Week (Mon–Sun)'},
  {value: 'last_week', label: 'Last Week (Mon–Sun)'},
  {value: 'this_year', label: 'This Year'},
  {value: 'last_year', label: 'Last Year'},
  {value: 'custom', label: 'Custom'},
];

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> =
  Object.fromEntries(
    DATE_RANGE_PRESETS.map(({value, label}) => [value, label])
  ) as Record<DateRangePreset, string>;

const FMT = 'YYYY-MM-DD';

// ---------------------------------------------------------------------------
// Detection-panel duration presets
// ---------------------------------------------------------------------------

export type DetectionDurationPreset = 'day' | 'week' | 'month' | 'year' | 'custom';

export const DETECTION_DURATION_PRESETS: Array<{
  value: DetectionDurationPreset;
  label: string;
  /** Calendar days in the window; 0 for custom. */
  days: number;
}> = [
  {value: 'day', label: 'Day', days: 1},
  {value: 'week', label: 'Week (7 Days)', days: 7},
  {value: 'month', label: 'Month (30 Days)', days: 30},
  {value: 'year', label: 'Year (365 Days)', days: 365},
  {value: 'custom', label: 'Custom', days: 0},
];

/**
 * Resolves a relative duration to concrete current + previous windows.
 *
 * Both windows end *yesterday* (full completed days) so today's in-progress
 * data doesn't skew the count. Returns null for 'custom'.
 *
 * Example (today = 2026-10-30, preset = 'week'):
 *   current  2026-10-23 – 2026-10-29
 *   previous 2026-10-16 – 2026-10-22
 */
export const resolveDurationPreset = (
  preset: DetectionDurationPreset
): {
  current: {date_start: string; date_end: string};
  previous: {date_start: string; date_end: string};
  days: number;
} | null => {
  const entry = DETECTION_DURATION_PRESETS.find((p) => p.value === preset);
  if (!entry || preset === 'custom') {
    return null;
  }
  const n = entry.days;
  const currentEnd = dayjs().subtract(1, 'day');
  const currentStart = currentEnd.subtract(n - 1, 'day');
  const prevEnd = currentStart.subtract(1, 'day');
  const prevStart = prevEnd.subtract(n - 1, 'day');
  return {
    current: {
      date_start: currentStart.format(FMT),
      date_end: currentEnd.format(FMT),
    },
    previous: {
      date_start: prevStart.format(FMT),
      date_end: prevEnd.format(FMT),
    },
    days: n,
  };
};

/**
 * Resolves a DateRangePreset to concrete YYYY-MM-DD boundaries.
 *
 * Mirrors the backend's resolveRelativeDateRange() in
 * backend/src/services/Helper/date.ts. Keep token semantics in sync.
 *
 * For 'custom', pass the literal customStart / customEnd strings. If they are
 * absent the result falls back to today for both bounds.
 *
 * Timezone: uses local dayjs() — same convention as individual panels. Company-tz
 * alignment is a planned follow-up.
 */
export const resolveDateRangePreset = (
  preset: DateRangePreset,
  customStart?: string | null,
  customEnd?: string | null
): {date_start: string; date_end: string} => {
  const today = dayjs();

  switch (preset) {
    case 'today':
      return {
        date_start: today.format(FMT),
        date_end: today.format(FMT),
      };
    case 'yesterday': {
      const yesterday = today.subtract(1, 'day');
      return {
        date_start: yesterday.format(FMT),
        date_end: yesterday.format(FMT),
      };
    }
    case 'last_7_days':
      return {
        date_start: today.subtract(6, 'day').format(FMT),
        date_end: today.format(FMT),
      };
    case 'last_30_days':
      return {
        date_start: today.subtract(29, 'day').format(FMT),
        date_end: today.format(FMT),
      };
    case 'this_week':
      return {
        date_start: today.startOf('isoWeek').format(FMT),
        date_end: today.endOf('isoWeek').format(FMT),
      };
    case 'last_week': {
      const lastWeekStart = today.subtract(1, 'week').startOf('isoWeek');
      return {
        date_start: lastWeekStart.format(FMT),
        date_end: lastWeekStart.endOf('isoWeek').format(FMT),
      };
    }
    case 'this_month':
      return {
        date_start: today.startOf('month').format(FMT),
        date_end: today.endOf('month').format(FMT),
      };
    case 'last_month': {
      const lastMonthStart = today.subtract(1, 'month').startOf('month');
      return {
        date_start: lastMonthStart.format(FMT),
        date_end: lastMonthStart.endOf('month').format(FMT),
      };
    }
    case 'custom':
      return {
        date_start: customStart ?? today.format(FMT),
        date_end: customEnd ?? today.format(FMT),
      };
    default:
      return {date_start: today.format(FMT), date_end: today.format(FMT)};
  }
};
